import React, { useEffect, useRef, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function Contact() {
  const pageRef = useRef(null);
  const heroRef = useRef(null);
  const formRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline();

      tl.from(".contact-eyebrow", {
        y: 25,
        opacity: 0,
        duration: 0.7,
        ease: "power3.out",
      })
        .from(".contact-title", {
          y: 70,
          opacity: 0,
          duration: 1,
          ease: "power4.out",
        }, "-=0.4")
        .from(".contact-description", {
          y: 30,
          opacity: 0,
          duration: 0.7,
          ease: "power3.out",
        }, "-=0.5")
        .from(formRef.current, {
          x: 80,
          opacity: 0,
          duration: 1,
          ease: "power4.out",
        }, "-=0.7");

      gsap.from(cardsRef.current, {
        scrollTrigger: {
          trigger: ".contact-info",
          start: "top 80%",
        },
        y: 50,
        opacity: 0,
        stagger: 0.15,
        duration: 0.8,
        ease: "power3.out",
      });

      // Floating background elements
      gsap.to(".orb-one", {
        x: 80,
        y: -40,
        duration: 6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".orb-two", {
        x: -60,
        y: 60,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });

      gsap.to(".contact-ring", {
        rotation: 360,
        duration: 20,
        repeat: -1,
        ease: "none",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  const addCardRef = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  // // Contact ------------------------->
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setStatus("");

    try {
      const response = await fetch(
        "https://middleeastfactories.com/contact.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (result.success) {
        setStatus("Your message has been sent successfully.");
        setFormData({
          name: "",
          email: "",
          message: "",
        });
      } else {
        setStatus(result.message || "Something went wrong.");
      }
    } catch (error) {
      console.error(error);
      setStatus("Unable to send your message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main >
      <section ref={pageRef} className="contact-page">
          {/* Background */}
      <div className="contact-background">
        <div className="orb orb-one"></div>
        <div className="orb orb-two"></div>
        <div className="contact-ring"></div>
      </div>

      <Container className="contact-container">

        {/* Hero */}
        <Row className="contact-hero">

          <Col lg={6} className="contact-copy">
            <span className="contact-eyebrow">
             Let's Build a Sustainable Future Together
            </span>

            <h1 className="contact-title">
              Looking for Sustainable<br/>Packaging Solutions?
              <br />
              <span>Let's talk.</span>
            </h1>

            <p className="contact-description">
              Whether you have a project in mind, need a creative partner,
              or simply want to say hello — we'd love to hear from you.
            </p>

            {/* Contact Info */}
           <div className="contact-info"  ref={addCardRef}>

  {/* Email */}
                <div
                  className="contact-info-card"
                >
                  <div className="info-icon">
                    ✉
                  </div>

                  <div>
                    <small>Email</small>
                    <a href="mailto:admin@middleeastfactories.com">
                      admin@middleeastfactories.com
                    </a>
                  </div>
                </div>

                {/* Phone */}
                <div
                  className="contact-info-card"
                >
                  <div className="info-icon">
                    ☎
                  </div>

                  <div>
                    <small>Phone</small>
                    <a href="tel:+919447257706">
                      +91 9447257706
                    </a>
                  </div>
                </div>

                {/* Address */}
                <div
                  className="contact-info-card"
                
                >
                  <div className="info-icon">
                    📍
                  </div>

                  <div>
                    <small>Office</small>

                    <a
                      href="https://www.google.com/maps/search/?api=1&query=Renga+Samudram+Village+Idaikal+Vilakku+Tenkasi+Tamilnadu+627804"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Renga Samudram Village, Idaikal Vilakku,
                      <br />
                      Tenkasi District, Tamil Nadu - 627804
                    </a>
                  </div>
                </div>

              </div>
          </Col>

          {/* Form */}
          <Col lg={6}>
            <div className="contact-form-wrapper" ref={formRef}>

              <div className="form-top">
                <span>START A PROJECT</span>
                <div className="form-dot"></div>
              </div>

            <Form onSubmit={handleSubmit}>
              <Form.Group className="form-group">
                <Form.Label>Your name</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Email address</Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                />
              </Form.Group>

              <Form.Group className="form-group">
                <Form.Label>Tell us about your project</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us a little about your idea..."
                  required
                />
              </Form.Group>

              <Button
                type="submit"
                className="contact-submit"
                disabled={loading}
              >
                <span>{loading ? "Sending..." : "Send message"}</span>
                {!loading && <span className="arrow">↗</span>}
              </Button>

              {status && (
                <div className="contact-status">
                  {status}
                </div>
              )}
            </Form>

            </div>
          </Col>
        </Row>
      </Container>
      </section> 
    
  {/* ///////// 
Map Details ------------------------------------------------------------------>
//////////////// */}
      <section className="map-section">

        {/* <div className="map-heading">
          <span>FIND US</span>
          <h2>Come <em>say hello.</em></h2>
       </div> */}

      <div className="map-wrapper">

        <iframe
          title="Middle East Factories Office Location"
          src="https://www.google.com/maps?q=Renga%20Samudram%20Village%2C%20Idaikal%20Vilakku%2C%20Tenkasi%2C%20Tamil%20Nadu%20627804&output=embed"
          width="100%"
          height="450"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />

        <a
          className="map-button"
          href="https://www.google.com/maps/search/?api=1&query=Renga+Samudram+Village+Idaikal+Vilakku+Tenkasi+Tamilnadu+627804"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in Google Maps
          <span>↗</span>
        </a>

      </div>
    </section>
    </main>
  );
}